import type { DatabaseRecord, FormField, FormResponseValue, FormResponseValueAnswer } from "@tellescope/types-models";
import { deriveComputedValueKey } from "./derive-computed-value-key";

export interface ResolveDatabasePrepopulationArgs {
  fieldsArr: FormField[];
  records: DatabaseRecord[];
  currentResponses: Record<string, FormResponseValue>;
}

const columnValue = (record: DatabaseRecord, label: string): unknown =>
  record.values.find((value) => value.label === label)?.value;

const isValueEmpty = (value: unknown) => value === undefined || value === null || value === "";

const ADDRESS_KEYS = ["addressLineOne", "addressLineTwo", "city", "state", "zipCode"];

const asAddress = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const address = Object.entries(value).reduce<Record<string, string>>((acc, [key, part]) => {
    if (ADDRESS_KEYS.includes(key) && typeof part === "string" && part !== "") acc[key] = part;
    return acc;
  }, {});
  return Object.keys(address).length ? address : undefined;
};

const coerceForFieldType = (type: FormField["type"], value: unknown): FormResponseValueAnswer["value"] | undefined => {
  switch (type) {
    case "string":
    case "stringLong":
    case "Timezone":
      if (typeof value === "string") return value;
      if (typeof value === "number") return String(value);
      return undefined;
    case "Address":
      return asAddress(value);
    default:
      return undefined;
  }
};

export const resolveDatabasePrepopulation = ({
  fieldsArr,
  records,
  currentResponses,
}: ResolveDatabasePrepopulationArgs): Record<string, FormResponseValue> =>
  fieldsArr.reduce<Record<string, FormResponseValue>>((acc, field) => {
    const config = field.prepopulateFromDatabase;
    if (!config?.field || !config.databaseId) return acc;

    const existing = currentResponses[field.id];
    if (!config.overwrite && !isValueEmpty(existing?.answer.value)) return acc;

    const match = records.find(
      (record) => record.databaseId === config.databaseId && !isValueEmpty(columnValue(record, config.field ?? ""))
    );
    if (!match) return acc;

    const coerced = coerceForFieldType(field.type, columnValue(match, config.field));
    if (coerced === undefined) return acc;

    // @ts-expect-error Union type gets in the way here
    const answer: FormResponseValueAnswer = { type: field.type, value: coerced };
    acc[field.id] = {
      ...existing,
      fieldId: field.id,
      fieldTitle: field.title,
      answer,
      computedValueKey: deriveComputedValueKey(field.intakeField, answer),
    };
    return acc;
  }, {});
